import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { normalizeAdsChunk } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.helpers';
import { AdOrder, GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { Ad, PlatformSource } from '@/api/types/common.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { mergeAdsResults } from '@/utils/helpers';

const EMPTY_ADS: Ad[] = [];

export interface VipAdsQueryVariables {
  orderType: AdOrder;
  adKind: ServiceName;
  first: AdsChunkSize;
  keywords: string | undefined;
  filters: Record<string, unknown>;
  source: PlatformSource;
}

interface UseVipAdsQueryOptions {
  variables: VipAdsQueryVariables;
  chunkSize: AdsChunkSize;
  skip: boolean;
}

export function useVipAdsQuery({
  variables,
  chunkSize,
  skip,
}: UseVipAdsQueryOptions) {
  const { data, loading, refetch, fetchMore, updateQuery } =
    useQuery<GetAdsResponse>(GET_ADS(ServiceName.Vip), {
      variables,
      notifyOnNetworkStatusChange: true,
      skip,
      onCompleted: completedData =>
        normalizeAdsChunk(completedData, chunkSize, updateQuery),
    });

  const nodes = data?.ads?.nodes ?? EMPTY_ADS;
  const endCursor = data?.ads?.pageInfo?.endCursor;
  const hasNextPage = data?.ads?.pageInfo?.hasNextPage ?? false;
  const isLoading = loading && !data;

  const refetchAndNormalize = useCallback(
    () =>
      refetch().then(({ data: refetched }) =>
        normalizeAdsChunk(refetched, chunkSize, updateQuery)
      ),
    [refetch, chunkSize, updateQuery]
  );

  const fetchMoreAds = useCallback(
    (after?: string) =>
      fetchMore({
        variables: after ? { after } : undefined,
        updateQuery: (prevResult, { fetchMoreResult }) =>
          mergeAdsResults(prevResult, fetchMoreResult, { chunkSize }),
      }),
    [fetchMore, chunkSize]
  );

  return {
    nodes,
    endCursor,
    hasNextPage,
    isLoading,
    refetchAndNormalize,
    fetchMoreAds,
  };
}
